import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { ORDER_PROCESS_STEPS, stepSkipped, type OrderProcessRow } from "@hisense/shared";
import { PageBody } from "@/components/ui/PageHeader";
import { Badge } from "@/components/ui/Badge";
import { QueryErrorState } from "@/components/ui/States";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { ExportButton } from "@/components/domain/ExportButton";
import { SeqBar, StateMark } from "@/components/process/ProcessMarks";
import { useOrderProcess } from "@/api/hooks";
import { getLocalizedError } from "@/lib/api";
import { formatDate } from "@/domain/datetime";

/** Nấc nào gồm những bước nào — dựng một lần từ khai báo dùng chung. */
const SEQS = [...new Set(ORDER_PROCESS_STEPS.map((s) => s.seq))].sort((a, b) => a - b);

/**
 * Quy trình thường (29/10/2026) — đơn KHÔNG chạy quy trình P đang ở bước nào. Cùng cách đọc và cùng hình dạng với màn Quy trình P
 * (`PProcessPage`): khách xin "cũng phải hiển thị từng bước như vậy, để người dùng có thể kiểm tra dễ dàng". Chỉ đọc.
 */
export function OrderProcessPage() {
  const { t } = useTranslation();
  const query = useOrderProcess();
  const [open, setOpen] = useState<string | null>(null);
  usePageCrumbs([{ label: t("orderProcess.title") }]);

  const rows = query.data?.rows ?? [];
  const label = (code: string) => t(`orderProcess.steps.${code}`);
  const sourcing = (r: OrderProcessRow) =>
    [r.purchased && t("orderProcess.purchased"), r.produced && t("orderProcess.produced")].filter(Boolean).join(" + ") || t("orderProcess.fromStock");

  return (
    <PageBody>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">{t("orderProcess.title")}</h1>
          <p className="mt-1 max-w-3xl text-sm text-ink-soft">{t("orderProcess.intro")}</p>
          <Link to="/p-process" className="mt-1 inline-block text-sm text-brand hover:underline">{t("orderProcess.pLink")}</Link>
        </div>
        {/* Một cột cho mỗi bước, như màn P: câu hỏi mang bảng này đi hỏi là "đơn nào kẹt ở bước nào". */}
        <ExportButton<OrderProcessRow>
          name="order-process"
          title={t("orderProcess.title")}
          rows={rows}
          disabled={query.isFetching}
          errorKey="orderProcess.error"
          rowKey={(r) => r.piId}
          columns={[
            { header: t("orderProcess.piNo"), value: (r) => r.piNo },
            { header: t("orderProcess.customer"), value: (r) => r.customerName },
            { header: t("orderProcess.date"), value: (r) => (r.date ? formatDate(r.date) : null) },
            { header: t("orderProcess.sourcing"), value: (r) => sourcing(r) },
            { header: t("orderProcess.current"), value: (r) => (r.currentStep ? label(r.currentStep) : null) },
            { header: t("orderProcess.progress"), value: (r) => `${r.doneCount}/${r.totalCount}` },
            { header: t("orderProcess.complete"), value: (r) => t(r.complete ? "common.yes" : "common.no") },
            ...ORDER_PROCESS_STEPS.map((step) => ({
              header: label(step.code),
              value: (r: OrderProcessRow) => {
                const state = r.steps.find((x) => x.code === step.code);
                return state ? t(`pProcess.states.${state.state}`) : null;
              },
            })),
          ]}
        />
      </div>

      {((query.data?.unlinked.orders ?? 0) > 0 || (query.data?.unlinked.purchaseOrders ?? 0) > 0) && (
        <p className="mb-4 flex items-start gap-2 rounded-md border border-warn/40 bg-warn/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" aria-hidden />
          <span>
            {(query.data?.unlinked.orders ?? 0) > 0 && (
              <span className="block font-medium text-ink">{t("pProcess.unlinked", { count: query.data?.unlinked.orders ?? 0 })}</span>
            )}
            {(query.data?.unlinked.purchaseOrders ?? 0) > 0 && (
              <span className="block font-medium text-ink">{t("pProcess.unlinkedPo", { count: query.data?.unlinked.purchaseOrders ?? 0 })}</span>
            )}
            <span className="block text-xs text-ink-soft">{t("pProcess.unlinkedHint")}</span>
          </span>
        </p>
      )}

      {query.error ? (
        <QueryErrorState
          error={query.error}
          message={getLocalizedError(query.error, t, "orderProcess.error")}
          forbiddenMessage={t("errors.forbidden")}
          onRetry={() => void query.refetch()}
        />
      ) : rows.length === 0 && !query.isLoading ? (
        <p className="rounded-md border border-line bg-surface p-6 text-center text-sm text-ink-soft">{t("orderProcess.empty")}</p>
      ) : (
        <div className="space-y-3">
          {query.data?.truncated ? <p className="text-xs text-ink-soft">{t("orderProcess.truncated", { count: rows.length })}</p> : null}
          {rows.map((row) => {
            const expanded = open === row.piId;
            const byCode = Object.fromEntries(row.steps.map((s) => [s.code, s]));
            return (
              <div key={row.piId} className="rounded-md border border-line bg-surface">
                <button
                  type="button"
                  className="flex w-full flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-left"
                  onClick={() => setOpen(expanded ? null : row.piId)}
                  aria-expanded={expanded}
                >
                  <span className="font-mono tabular text-sm font-medium text-ink">{row.piNo}</span>
                  <span className="text-sm text-ink-soft">{row.customerName ?? "—"}</span>
                  <span className="font-mono tabular text-xs text-ink-faint">{row.date ? formatDate(row.date) : "—"}</span>
                  <SeqBar seqs={SEQS} steps={row.steps} />
                  <span className="font-mono tabular text-xs text-ink-soft">{row.doneCount}/{row.totalCount}</span>
                  {row.complete ? (
                    <Badge tone="ok">{t("orderProcess.complete")}</Badge>
                  ) : (
                    <Badge tone="warn" outline>
                      {t("orderProcess.current")}: {row.currentStep ? label(row.currentStep) : "—"}
                    </Badge>
                  )}
                  {row.outOfOrder.length > 0 && (
                    <Badge tone="danger" outline>
                      <AlertTriangle className="mr-1 inline size-3" aria-hidden />
                      {t("orderProcess.outOfOrder")}
                    </Badge>
                  )}
                </button>

                {expanded && (
                  <div className="border-t border-line px-4 py-3">
                    <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                      <Badge tone="neutral" outline>{t("orderProcess.sourcing")}: {sourcing(row)}</Badge>
                      <Badge tone="neutral" outline>{t(`orderProcess.settlement.${row.settlement}`)}</Badge>
                      <Link to={`/proforma-invoices/${row.piId}`} className="ml-auto text-brand hover:underline">{row.piNo}</Link>
                    </div>
                    {row.outOfOrder.length > 0 && (
                      <p className="mb-3 rounded-md border border-danger/40 bg-danger/5 p-2 text-xs text-ink-soft">{t("orderProcess.outOfOrderHint")}</p>
                    )}
                    <ol className="space-y-1">
                      {SEQS.map((seq) => (
                        <Fragment key={seq}>
                          {ORDER_PROCESS_STEPS.filter((s) => s.seq === seq).map((step) => {
                            const state = byCode[step.code];
                            if (!state) return null;
                            // Hai trường hợp "đang làm dở" phải nói ra, không để người xem tưởng chưa có gì.
                            const note = state.state === "pending" && step.code === "delivery" && row.deliveryStarted
                              ? t("orderProcess.deliveryStarted")
                              : state.state === "pending" && step.code === "payment" && row.settlement === "partial"
                                ? t("orderProcess.settlement.partial")
                                : null;
                            return (
                              <li key={step.code} className="flex flex-wrap items-center gap-2 border-b border-line/60 py-1.5 last:border-b-0">
                                <span className="flex w-6 justify-center"><StateMark state={state.state} /></span>
                                <span className="font-mono tabular text-xs text-ink-faint">{seq}</span>
                                <span className={stepSkipped(state.state) ? "text-sm text-ink-faint" : "text-sm text-ink"}>{label(step.code)}</span>
                                {step.optional ? <Badge tone="neutral" outline className="text-[10px]">{t("orderProcess.optional")}</Badge> : null}
                                {note ?<Badge tone="info" outline className="text-[10px]">{note}</Badge> : null}
                                <span className="ml-auto text-xs text-ink-faint">{t(`pProcess.states.${state.state}`)}</span>
                              </li>
                            );
                          })}
                        </Fragment>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </PageBody>
  );
}
